import type { ContactInput } from '@graphql/generated-base';
import type { ContactInputFields } from '../types';

/**
 * Narrow type representing the contact fields needed for form prefill.
 * Both the pid flow (ContactByFirebaseId.contact) and guestKey flow (Contact)
 * satisfy this shape.
 *
 * TODO: guestKey query param support — structure is ready to accept the flat Contact
 * shape from getContactsByGuestKeyList once BE supports it in the contact collector context.
 */
export type ContactForPrefill = Readonly<{
  firstName: string;
  lastName: string;
  email?: string | null;
  phoneNumber?: ReadonlyArray<{ number: string } | null> | null;
  household?: {
    address?: {
      address1: string;
      address2?: string | null;
      city: string;
      region: string;
      postalCode: string;
      country: string;
      countryCode: string;
      validated: boolean;
    } | null;
  } | null;
}>;

/**
 * Maps a flat contact object to form field values for pre-filling the contact collector form.
 * Works with both pid-resolved contacts (via ContactByFirebaseId.contact) and
 * guestKey-resolved contacts (via getContactsByGuestKeyList).
 */
export const mapContactToFormValues = (contact: ContactForPrefill): ContactInputFields => {
  const address = contact.household?.address;

  return {
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email ?? '',
    phoneNumber: contact.phoneNumber?.[0]?.number ?? '',
    address1: address?.address1 ?? '',
    address2: address?.address2 ?? '',
    city: address?.city ?? '',
    region: address?.region ?? '',
    postalCode: address?.postalCode ?? '',
    country: address?.country ?? 'United States',
    countryCode: address?.countryCode ?? 'US',
    validated: address?.validated ?? false,
  };
};

/**
 * Maps form values to a ContactInput for the updateContact mutation.
 */
export const mapFormValuesToUpdateInput = (values: ContactInputFields, eventId: string, firebaseEventId: string): ContactInput => ({
  address: {
    address1: values.address1,
    address2: values.address2,
    city: values.city,
    country: values.country,
    countryCode: values.countryCode,
    postalCode: values.postalCode,
    region: values.region,
    validated: values.validated,
  },
  contact: {
    firstName: (values.firstName ?? '').trim(),
    lastName: (values.lastName ?? '').trim(),
    email: (values.email ?? '').trim(),
    eventId,
    firebaseEventId,
  },
  phoneNumbers: values.phoneNumber
    ? [
        {
          allowSMS: true,
          mobile: true,
          number: values.phoneNumber.trim(),
        },
      ]
    : undefined,
});
